import axios from 'axios';
import { FormRenderResponse, ConsentFormData, ConsentSubmissionResponse } from '../types/consent';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

export interface LoginRequest {
  tenantId?: string;      // Deprecated - use tenantSlug
  tenantSlug?: string;    // Preferred - human-readable tenant identifier
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  userId?: string;
  name?: string;
  email?: string;
  role?: string;
  tenantId?: string;
  tenantSlug?: string;
  // Legacy user object for backward compatibility
  user?: {
    userId?: string;
    name?: string;
    email: string;
    role: string;
    tenantId?: string;
    tenantSlug?: string;
  };
}

export interface TenantPublicInfo {
  tenantId: string;
  name: string;
  slug: string;
  logo?: string;
}

export interface PlatformPolicy {
  policyType: string;
  templateId: string;
  title: string;
  content: string;
  version: string;
  required: boolean;
}

export interface PlatformConsentStatus {
  userEmail: string;
  privacyPolicyAccepted: boolean;
  privacyPolicyVersion?: string;
  currentPrivacyPolicyVersion: string;
  termsOfServiceAccepted: boolean;
  termsOfServiceVersion?: string;
  currentTermsOfServiceVersion: string;
  requiresReconsent: boolean;
}

export interface RecordPlatformConsentRequest {
  userEmail: string;
  privacyPolicyAccepted: boolean;
  termsOfServiceAccepted: boolean;
  deviceInfo?: string;
}

export interface OrganizationSignupRequest {
  organizationName: string;
  organizationWebsite?: string;
  organizationType?: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  contactRole?: string;
  expectedUsers?: string;
  useCase?: string;
  privacyPolicyAccepted: boolean;
  termsOfServiceAccepted: boolean;
}

export interface OrganizationSignupResponse {
  requestId: string;
  status: string;
  message: string;
}

export async function getConsentForm(tenantId: string, urlId: string, token: string): Promise<FormRenderResponse> {
  const response = await axios.get(
    `${API_BASE_URL}/consent/v1/forms/${tenantId}/${urlId}/render`,
    {
      params: { token }
    }
  );
  return response.data;
}

export async function submitConsent(
  tenantId: string,
  urlId: string,
  token: string,
  data: ConsentFormData
): Promise<ConsentSubmissionResponse> {
  const response = await axios.post(
    `${API_BASE_URL}/consent/v1/forms/${tenantId}/${urlId}/submit`,
    data,
    {
      params: { token }
    }
  );
  return response.data;
}

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await axios.post<LoginResponse>(`${API_BASE_URL}/auth/v1/admin/login`, credentials, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

/**
 * Resolve tenant by slug - get public tenant info for branding.
 */
export async function resolveTenant(slug: string): Promise<TenantPublicInfo> {
  const response = await axios.get<TenantPublicInfo>(`${API_BASE_URL}/api/v1/public/tenant/resolve/${slug}`);
  return response.data;
}

/**
 * Get current platform policies (privacy policy, terms of service).
 */
export async function getPlatformPolicies(): Promise<PlatformPolicy[]> {
  const response = await axios.get<PlatformPolicy[]>(`${API_BASE_URL}/api/v1/platform/consent/policies`);
  return response.data;
}

/**
 * Check user's platform consent status.
 */
export async function checkPlatformConsentStatus(email: string): Promise<PlatformConsentStatus> {
  const response = await axios.get<PlatformConsentStatus>(`${API_BASE_URL}/api/v1/platform/consent/status`, {
    params: { email },
  });
  return response.data;
}

/**
 * Record platform consent (during signup or re-consent).
 */
export async function recordPlatformConsent(request: RecordPlatformConsentRequest): Promise<{ consentId: string }> {
  const response = await axios.post<{ consentId: string }>(`${API_BASE_URL}/api/v1/platform/consent/record`, request, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

/**
 * Submit organization signup request (new tenant onboarding).
 * This creates a pending request for super admin review.
 */
export async function submitOrganizationSignup(request: OrganizationSignupRequest): Promise<OrganizationSignupResponse> {
  const response = await axios.post<OrganizationSignupResponse>(
    `${API_BASE_URL}/api/v1/public/tenant/signup`,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
}

// ========================================
// Public Invitation APIs
// ========================================

export interface InvitationDetails {
  invitation: {
    invitationId: string;
    tenantId: string;
    email: string;
    name?: string;
    role: string;
    projectId?: string;
    status: string;
    invitedBy?: string;
    invitedByName?: string;
    invitedAt?: string;
    expiresAt?: string;
  };
  tenantName: string;
  tenantSlug: string;
}

export interface AcceptInvitationRequest {
  token: string;
  password: string;
  name?: string;
  privacyPolicyAccepted: boolean;
  termsOfServiceAccepted: boolean;
}

export interface AcceptInvitationResponse {
  message: string;
  user: {
    userId: string;
    tenantId: string;
    email: string;
    name?: string;
    role: string;
  };
  tenantSlug: string;
  tenantId: string;
}

/**
 * Get invitation details by token (for accept invitation page).
 */
export async function getInvitationByToken(token: string): Promise<InvitationDetails> {
  const response = await axios.get<InvitationDetails>(`${API_BASE_URL}/api/v1/public/invitation/${token}`);
  return response.data;
}

/**
 * Accept an invitation and create user account.
 */
export async function acceptInvitation(request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
  const response = await axios.post<AcceptInvitationResponse>(
    `${API_BASE_URL}/api/v1/public/invitation/accept`,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
}

// ========================================
// Password Reset APIs
// ========================================

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
  email?: string;
  message?: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

/**
 * Request a password reset email.
 */
export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
  const response = await axios.post<ForgotPasswordResponse>(
    `${API_BASE_URL}/api/v1/auth/forgot-password`,
    { email },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
}

/**
 * Validate a password reset token.
 */
export async function validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
  const response = await axios.get<ValidateResetTokenResponse>(
    `${API_BASE_URL}/api/v1/auth/reset-password/validate`,
    { params: { token } }
  );
  return response.data;
}

/**
 * Reset password using a valid token.
 */
export async function resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const response = await axios.post<ResetPasswordResponse>(
    `${API_BASE_URL}/api/v1/auth/reset-password`,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
}

export const consentApi = {
  renderForm: getConsentForm,
  getConsentForm,
  submitConsent,
  login,
  resolveTenant,
  getPlatformPolicies,
  checkPlatformConsentStatus,
  recordPlatformConsent,
  submitOrganizationSignup,
  getInvitationByToken,
  acceptInvitation,
  forgotPassword,
  validateResetToken,
  resetPassword,
};
